import { useNavigate } from 'react-router-dom';

import './styles/legal.css';

// TSS vite/01 — página legal PÚBLICA (sin auth ni shell): política de
// privacidad + tratamiento de datos (Habeas Data, Ley 1581/2012) + términos.
// Debe poder leerse sin sesión (requisito de habeas data). Es una PLANTILLA:
// los textos entre [corchetes] y el aviso superior deben validarse con asesoría
// legal y reemplazarse por los datos reales de la empresa antes de publicar.

const UPDATED = '3 de julio de 2026';

export default function LegalPage() {
    const navigate = useNavigate();

    return (
        <div className="legal">
            <header className="legal__topbar">
                <span className="legal__brand">
                    <img className="legal__logo" src="/cowre.png" alt="" aria-hidden="true" />
                    <span className="legal__wordmark">COWRE</span>
                </span>
                <button type="button" className="legal__back" onClick={() => navigate(-1)}>
                    ← Volver
                </button>
            </header>

            <main className="legal__doc">
                <h1 className="legal__title">Políticas y términos</h1>
                <p className="legal__meta">Última actualización: {UPDATED}</p>

                <p className="legal__notice">
                    <strong>Aviso:</strong> este documento es una <strong>plantilla base</strong> y debe ser
                    revisado y validado por asesoría legal, reemplazando los campos entre [corchetes] con los
                    datos reales de la empresa, antes de considerarse la versión oficial.
                </p>

                <nav className="legal__toc" aria-label="Contenido">
                    <a href="#privacidad">1. Política de Privacidad</a>
                    <a href="#datos">2. Tratamiento de Datos Personales</a>
                    <a href="#terminos">3. Términos y Condiciones</a>
                </nav>

                {/* 1 — Privacidad */}
                <section id="privacidad" className="legal__section">
                    <h2>1. Política de Privacidad</h2>
                    <p>
                        Esta política describe cómo <strong>[Razón social]</strong> (NIT <strong>[NIT]</strong>),
                        operadora del portal de clientes <strong>COWRE</strong>, recolecta, usa y protege la
                        información de sus clientes.
                    </p>

                    <h3>Datos que recolectamos</h3>
                    <ul>
                        <li><strong>Identificación y contacto:</strong> nombre, correo, teléfono y documento.</li>
                        <li><strong>Datos de la relación ganadera:</strong> parcerías, cowhousing, liquidaciones y estadísticas asociadas a tu cuenta.</li>
                        <li><strong>Datos técnicos:</strong> registros de acceso y cookies estrictamente necesarias.</li>
                    </ul>

                    <h3>Cookies</h3>
                    <p>
                        Usamos únicamente cookies <strong>necesarias</strong> para el funcionamiento y la seguridad:
                        una cookie de sesión (httpOnly, no legible por scripts) y una cookie de protección CSRF.
                        No usamos cookies de publicidad ni de seguimiento de terceros.
                    </p>

                    <h3>Finalidad y conservación</h3>
                    <p>
                        Tratamos tus datos para prestarte el servicio del portal, mostrarte tu información ganadera
                        y económica, garantizar la seguridad y cumplir obligaciones legales. Los conservamos mientras
                        exista la relación y por los plazos que exija la ley.
                    </p>

                    <h3>Seguridad y terceros</h3>
                    <p>
                        Aplicamos medidas técnicas y organizativas razonables (cifrado en tránsito, control de acceso
                        por rol y aislamiento por cliente). <strong>No vendemos</strong> tus datos; solo los
                        compartimos con proveedores necesarios para operar el servicio y con autoridades cuando la
                        ley lo exige.
                    </p>
                </section>

                {/* 2 — Tratamiento de datos / Habeas Data */}
                <section id="datos" className="legal__section">
                    <h2>2. Tratamiento de Datos Personales (Habeas Data)</h2>
                    <p>
                        En cumplimiento de la <strong>Ley 1581 de 2012</strong> y el <strong>Decreto 1074 de 2015</strong>
                        {' '}de la República de Colombia, informamos lo siguiente sobre el tratamiento de datos personales.
                    </p>

                    <h3>Responsable del tratamiento</h3>
                    <p>
                        <strong>[Razón social]</strong> · NIT <strong>[NIT]</strong> · <strong>[Dirección]</strong> ·
                        Correo de protección de datos: <strong>[correo@empresa.com]</strong> · Tel: <strong>[teléfono]</strong>.
                    </p>

                    <h3>Finalidades</h3>
                    <p>
                        Gestión de la relación comercial y ganadera, facturación y liquidaciones, comunicación con el
                        cliente, y cumplimiento de obligaciones legales y contractuales.
                    </p>

                    <h3>Derechos del titular</h3>
                    <p>Como titular de tus datos, tienes derecho a:</p>
                    <ul>
                        <li>Conocer, actualizar y rectificar tus datos.</li>
                        <li>Solicitar prueba de la autorización otorgada.</li>
                        <li>Ser informado sobre el uso dado a tus datos.</li>
                        <li>Presentar quejas ante la Superintendencia de Industria y Comercio (SIC).</li>
                        <li>Revocar la autorización y/o solicitar la supresión cuando proceda legalmente.</li>
                        <li>Acceder de forma gratuita a tus datos.</li>
                    </ul>

                    <h3>Cómo ejercer tus derechos</h3>
                    <p>
                        Envía tu solicitud a <strong>[correo@empresa.com]</strong> indicando tu nombre, identificación,
                        la descripción de tu petición y tus datos de contacto. Atenderemos las consultas y reclamos en
                        los plazos de ley (consultas: 10 días hábiles; reclamos: 15 días hábiles). Al usar el portal
                        autorizas el tratamiento de tus datos conforme a esta política.
                    </p>
                </section>

                {/* 3 — Términos y condiciones */}
                <section id="terminos" className="legal__section">
                    <h2>3. Términos y Condiciones</h2>

                    <h3>Objeto y cuenta</h3>
                    <p>
                        Estos términos regulan el uso del portal de clientes COWRE. Eres responsable de la
                        confidencialidad de tus credenciales y de la actividad realizada en tu cuenta; notifícanos de
                        inmediato cualquier acceso no autorizado.
                    </p>

                    <h3>Uso aceptable</h3>
                    <ul>
                        <li>No intentar vulnerar la seguridad del portal ni acceder a datos de otros clientes.</li>
                        <li>No usar el portal para fines ilícitos o no autorizados.</li>
                    </ul>

                    <h3>Información e integridad</h3>
                    <p>
                        Los datos mostrados (KPIs, valorizaciones, liquidaciones) son de carácter informativo. Ante
                        cualquier discrepancia prevalecen los registros oficiales de <strong>[Razón social]</strong>.
                    </p>

                    <h3>Propiedad intelectual y responsabilidad</h3>
                    <p>
                        El portal, su marca y contenidos pertenecen a <strong>[Razón social]</strong> / Trianametria y
                        no se concede más licencia que el uso previsto. El servicio se presta “tal cual”; no
                        garantizamos disponibilidad ininterrumpida y la responsabilidad se limita conforme a la ley.
                    </p>

                    <h3>Cambios, ley aplicable y contacto</h3>
                    <p>
                        Podemos actualizar estos términos; la fecha de “última actualización” refleja su vigencia.
                        Este documento se rige por las leyes de la República de Colombia, con domicilio contractual en
                        <strong>[ciudad]</strong>. Contacto: <strong>[correo@empresa.com]</strong> · <strong>[teléfono]</strong>.
                    </p>
                </section>

                <footer className="legal__footer">
                    © {UPDATED.slice(-4)} [Razón social] · COWRE — Portal de clientes.
                </footer>
            </main>
        </div>
    );
}
